import {
  type InsertProduct,
  ProductType,
  insertProductSchema,
  patchProductSchema,
} from "@/api/db/schema/product";
import { productService } from "@/api/services/product.service";
import { pushSubscriptionService } from "@/api/services/push-subscription.service";
import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { authMiddlewareServer } from "../middleware/auth";

// PUBLIC ENDPOINTS (for store page - no authentication required)

export const listPublicProductsFn = createServerFn({ method: "GET" }).handler(
  async () => {
    const allProducts = await productService.listProducts();

    // Group by type - flatten data blob for frontend
    const mapProduct = (p: (typeof allProducts)[number]) => {
      const data = (p.data || {}) as Partial<typeof p.data>;
      return {
        name: data.name || "",
        description: data.description || "",
        price: data.price || 0,
        oldPrice: data.oldPrice,
        imageUrl: data.imageUrl,
        sizes: data.sizes || [],
        colors: data.colors || [],
        stock: data.stock,
        isFeatured: data.isFeatured || false,
        productId: p.productId,
        productType: p.productType,
        isActive: p.isActive,
        displayOrder: p.displayOrder,
      };
    };

    const tshirts = allProducts
      .filter((p) => p.productType === ProductType.TSHIRT)
      .map(mapProduct);

    const weights = allProducts
      .filter((p) => p.productType === ProductType.WEIGHTS)
      .map(mapProduct);

    const equipment = allProducts
      .filter((p) => p.productType === ProductType.EQUIPMENT)
      .map(mapProduct);

    const accessories = allProducts
      .filter((p) => p.productType === ProductType.ACCESSORIES)
      .map(mapProduct);

    const other = allProducts
      .filter((p) => p.productType === ProductType.OTHER)
      .map(mapProduct);

    return {
      status: 200,
      body: {
        tshirts,
        weights,
        equipment,
        accessories,
        other,
        all: allProducts.map(mapProduct),
        total: allProducts.length,
      },
    };
  },
);

export const listPublicProductsOptions = () =>
  queryOptions({
    queryKey: ["listPublicProducts"],
    queryFn: () => listPublicProductsFn(),
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

// ADMIN ENDPOINTS (require admin authentication)

export const listAllProductsFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .handler(async ({ context }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const allProducts = await productService.listAllProducts();

    return {
      status: 200,
      body: {
        products: allProducts,
        total: allProducts.length,
      },
    };
  });

export const listAllProductsOptions = () =>
  queryOptions({
    queryKey: ["listAllProducts"],
    queryFn: () => listAllProductsFn(),
  });

const getProductByIdSchema = z.object({
  productId: z.string(),
});

export const getProductByIdFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(getProductByIdSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const product = await productService.getProductById(data.productId);

    if (!product) {
      return {
        status: 404,
        body: { message: "Product not found" },
      };
    }

    return {
      status: 200,
      body: product,
    };
  });

export const createProductFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(insertProductSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const product = await productService.createProduct(data as InsertProduct);

    void pushSubscriptionService.sendNotificationToAllFiltered(
      "notifNewContent",
      {
        title: "Yeni Ürün",
        body: product.data.name,
        url: "/store",
      },
    );

    return {
      status: 201,
      body: {
        message: "Product created successfully",
        product,
      },
    };
  });

export const updateProductFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(patchProductSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const { productId, ...updateData } = data;
    const product = await productService.updateProduct(productId, updateData);

    if (!product) {
      return {
        status: 404,
        body: { message: "Product not found" },
      };
    }

    return {
      status: 200,
      body: {
        message: "Product updated successfully",
        product,
      },
    };
  });

const deleteProductSchema = z.object({
  productId: z.string(),
});

export const deleteProductFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(deleteProductSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const product = await productService.deleteProduct(data.productId);

    if (!product) {
      return {
        status: 404,
        body: { message: "Product not found" },
      };
    }

    return {
      status: 200,
      body: {
        message: "Product deleted successfully",
      },
    };
  });

const toggleSchema = z.object({
  productId: z.string(),
});

export const toggleActiveFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(toggleSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const product = await productService.toggleActive(data.productId);

    if (!product) {
      return {
        status: 404,
        body: { message: "Product not found" },
      };
    }

    return {
      status: 200,
      body: {
        message: `Product ${product.isActive ? "activated" : "deactivated"}`,
        product,
      },
    };
  });

export const toggleFeaturedFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(toggleSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const product = await productService.toggleFeatured(data.productId);

    if (!product) {
      return {
        status: 404,
        body: { message: "Product not found" },
      };
    }

    return {
      status: 200,
      body: {
        message: `Product ${product.data.isFeatured ? "marked as featured" : "unmarked as featured"}`,
        product,
      },
    };
  });
